import React, { useState, useEffect, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Button,
  Badge,
  Card,
  Modal,
  Form,
  Image,
  OverlayTrigger,
  Tooltip,
  Row,
  Col,
  Table,
  ProgressBar,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaPlus,
  FaSearch,
  FaUserTie,
  FaCalendarAlt,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaFileSignature,
  FaFilter,
  FaFileAlt,
  FaFileInvoiceDollar,
  FaSignature,
} from "react-icons/fa";
import { FiMoreVertical } from "react-icons/fi";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jwtDecode } from "jwt-decode";

// Constants
const DEVELOPERS = ["John Doe", "Jane Smith", "Emily Wilson", "Michael Brown"];
const API_BASE_URL = "http://localhost:5890";

const QuotationTable = ({ customerId }) => {
  // State management
  const [state, setState] = useState({
    quotations: [],
    loading: true,
    error: null,
    showModal: false,
    showAddModal: false,
    selectedQuote: null,
    signature: null,
    selectedDeveloper: "",
    searchTerm: "",
    filterStatus: "All",
    authToken: "",
    customerIDDetails: "",
  });

  const [newQuote, setNewQuote] = useState({
    title: "",
    status: "Requested",
    date: "",
    validUntil: "",
    description: "",
    developer: "",
    amount: "",
    customerId: "",
  });

  // Helper function to update state
  const updateState = (newState) => {
    setState((prev) => ({ ...prev, ...newState }));
  };

  // Status badge component
  const getStatusBadge = (status) => {
    const statusConfig = {
      Requested: { icon: <FaClock />, variant: "warning", text: "dark" },
      Approved: { icon: <FaCheckCircle />, variant: "success" },
      Rejected: { icon: <FaTimesCircle />, variant: "danger" },
      "In Progress": { icon: <FaClock />, variant: "primary" },
    };

    const config = statusConfig[status] || { variant: "secondary" };

    return (
      <Badge
        bg={config.variant}
        text={config.text}
        className="px-3 py-2 rounded-pill d-flex align-items-center gap-2"
      >
        {config.icon} {status}
      </Badge>
    );
  };

  // Token handling and initialization
  useEffect(() => {
    const initializeAuth = async () => {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("No authentication token found");
        return;
      }

      try {
        const decoded = jwtDecode(token);
        updateState({
          authToken: token,
          customerIDDetails: decoded.id,
        });
        setNewQuote((prev) => ({ ...prev, customerId: decoded.id }));
      } catch (error) {
        console.error("Error decoding token:", error);
        toast.error("Invalid token format");
        sessionStorage.removeItem("token");
      }
    };

    initializeAuth();
  }, []);

  // Configure axios defaults
  useEffect(() => {
    if (state.authToken) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${state.authToken}`;
    }
  }, [state.authToken]);

  // Fetch quotations
  const fetchQuotations = useCallback(async () => {
    if (!state.customerIDDetails || !state.authToken) return;

    try {
      updateState({ loading: true, error: null });
      const response = await axios.get(
        `${API_BASE_URL}/GetcustomerQuestion/${state.customerIDDetails}`
      );
      updateState({ quotations: response.data });
      toast.success("Quotations loaded successfully");
    } catch (err) {
      console.error("Error fetching quotations:", err);
      updateState({ error: "Failed to fetch quotations" });
      toast.error("Failed to load quotations. Please try again.");
    } finally {
      updateState({ loading: false });
    }
  }, [state.customerIDDetails, state.authToken]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  // Modal handlers
  const handleOpenModal = (quote) => {
    updateState({
      selectedQuote: quote,
      selectedDeveloper: quote.developer || "",
      showModal: true,
    });
  };

  const handleCloseModal = () => {
    updateState({
      selectedQuote: null,
      signature: null,
      selectedDeveloper: "",
      showModal: false,
    });
  };

  // Event handlers
  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      updateState({ signature: URL.createObjectURL(file) });
      toast.info("Signature uploaded successfully");
    }
  };

  const handleDeveloperSelect = (e) => {
    updateState({ selectedDeveloper: e.target.value });
  };

  const handleNewQuoteChange = (e) => {
    const { name, value } = e.target;
    setNewQuote((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNewQuote = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/addcustomeradddetails`,
        newQuote
      );
      updateState({ quotations: [...state.quotations, response.data] });
      setNewQuote({
        title: "",
        status: "Requested",
        date: "",
        validUntil: "",
        description: "",
        developer: "",
        amount: "",
        customerId: state.customerIDDetails,
      });
      updateState({ showAddModal: false });
      toast.success("Quotation added successfully");
    } catch (err) {
      console.error("Error adding new quotation:", err);
      toast.error("Failed to add new quotation");
    }
  };

  // Filter and statistics
  const filteredQuotations = state.quotations.filter((q) => {
    const matchesSearch = q.title.toLowerCase().includes(state.searchTerm.toLowerCase()) || 
                         q.description.toLowerCase().includes(state.searchTerm.toLowerCase());
    const matchesStatus = state.filterStatus === "All" || q.status === state.filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    requested: state.quotations.filter(q => q.status === "Requested").length,
    approved: state.quotations.filter(q => q.status === "Approved").length,
    rejected: state.quotations.filter(q => q.status === "Rejected").length,
    inProgress: state.quotations.filter(q => q.status === "In Progress").length,
    total: state.quotations.length
  };

  // Render loading state
  if (state.loading) {
    return (
      <div className="container-fluid py-4 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading quotations...</p>
      </div>
    );
  }

  // Render error state
 

  return (
    <div className="container-fluid py-4">
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div>
              <h3 className="fw-bold mb-1 d-flex align-items-center gap-3">
                <FaFileInvoiceDollar className="text-primary" />
                Quotation Management
              </h3>
              <p className="text-muted mb-0">Track and manage all client quotations</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="primary"
                onClick={() => updateState({ showAddModal: true })}
                className="d-flex align-items-center gap-2 px-4 py-2 fw-semibold"
              >
                <FaPlus /> New Quotation
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4 g-3">
        {[
          { title: "Total Quotations", value: stats.total, icon: <FaFileInvoiceDollar />, color: "primary" },
          { title: "Requested", value: stats.requested, icon: <FaClock />, color: "warning" },
          { title: "Approved", value: stats.approved, icon: <FaCheckCircle />, color: "success" },
          { title: "In Progress", value: stats.inProgress, icon: <FaClock />, color: "info" },
        ].map((stat, index) => (
          <div key={index} className="col-md-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">{stat.title}</h6>
                    <h3 className="mb-0">{stat.value}</h3>
                  </div>
                  <div className={`bg-${stat.color} bg-opacity-10 p-3 rounded`}>
                    {React.cloneElement(stat.icon, { className: `text-${stat.color}`, size: 20 })}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="row mb-4 g-3">
        <div className="col-md-8">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search quotations by title or description..."
              value={state.searchTerm}
              onChange={(e) => updateState({ searchTerm: e.target.value })}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <FaFilter className="text-muted" />
            </span>
            <Form.Select 
              value={state.filterStatus} 
              onChange={(e) => updateState({ filterStatus: e.target.value })}
            >
              <option value="All">All Statuses</option>
              <option value="Requested">Requested</option>
              <option value="Approved">Approved</option>
              <option value="In Progress">In Progress</option>
              <option value="Rejected">Rejected</option>
            </Form.Select>
          </div>
        </div>
      </div>

      {/* Quotations Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {filteredQuotations.length === 0 ? (
            <div className="text-center py-5">
              <Alert variant="info" className="mx-4">
                No quotations found matching your criteria.
              </Alert>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Developer</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotations.map((q) => (
                    <tr key={q._id} className="align-middle">
                      <td className="fw-semibold">#{q._id.substring(0, 6)}</td>
                      <td>
                        <div className="fw-semibold">{q.title}</div>
                        <small className="text-muted">
                          <FaCalendarAlt className="me-1" />
                          Valid until: {q.validUntil}
                        </small>
                      </td>
                      <td>
                        <div className="text-muted" style={{ maxWidth: "300px" }}>
                          {q.description.length > 60 
                            ? `${q.description.substring(0, 60)}...` 
                            : q.description}
                        </div>
                      </td>
                      <td>
                        {q.developer ? (
                          <div className="d-flex align-items-center gap-2">
                            <div className="bg-primary bg-opacity-10 p-2 rounded-circle">
                              <FaUserTie className="text-primary" />
                            </div>
                            <div className="fw-medium">{q.developer}</div>
                          </div>
                        ) : (
                          <Badge bg="secondary">Unassigned</Badge>
                        )}
                      </td>
                      <td className="fw-bold">{q.amount}</td>
                      <td>{getStatusBadge(q.status)}</td>
                  
                      <td>
                        <div className="text-muted small">{q.date}</div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View details</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenModal(q)}
                              className="d-flex align-items-center gap-1"
                            >
                              <FaEye /> View
                            </Button>
                          </OverlayTrigger>
                          {q.status === "Approved" && (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>E-sign this quotation</Tooltip>}
                            >
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleOpenModal(q)}
                                className="d-flex align-items-center gap-1"
                              >
                                <FaSignature /> Sign
                              </Button>
                            </OverlayTrigger>
                          )}
                          <Button variant="light" size="sm" className="rounded-circle">
                            <FiMoreVertical />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* View / E-sign Modal */}
      <Modal show={state.showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <FaFileAlt /> Quotation Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {state.selectedQuote && (
            <div className="row g-4">
              <div className="col-md-8">
                <div className="mb-4">
                  <h4 className="fw-bold">{state.selectedQuote.title}</h4>
                  <p className="text-muted">{state.selectedQuote.description}</p>
                </div>

                <div className="bg-light p-4 rounded-3 mb-4">
                  <h6 className="fw-bold mb-3">Quotation Summary</h6>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <div className="text-muted small mb-1">Status</div>
                        {getStatusBadge(state.selectedQuote.status)}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <div className="text-muted small mb-1">Amount</div>
                        <div className="fw-bold fs-5">{state.selectedQuote.amount}</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <div className="text-muted small mb-1">Created Date</div>
                        <div className="fw-medium">{state.selectedQuote.date}</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <div className="text-muted small mb-1">Valid Until</div>
                        <div className="fw-medium">{state.selectedQuote.validUntil}</div>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="mb-3">
                  <h6 className="fw-bold mb-3">Progress</h6>
                  <div className="d-flex align-items-center gap-3">
                    <ProgressBar 
                      now={state.selectedQuote.progress || 0} 
                      variant={
                        state.selectedQuote.progress > 80 ? "success" : 
                        state.selectedQuote.progress > 50 ? "primary" : "warning"
                      } 
                      style={{ width: "200px", height: "8px" }} 
                    />
                    <div className="fw-bold">{state.selectedQuote.progress || 0}% Complete</div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h6 className="fw-bold mb-3">Assign & Sign</h6>
                    
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        <FaUserTie className="me-2 text-primary" /> Assign Developer
                      </Form.Label>
                      <Form.Select
                        value={state.selectedDeveloper}
                        onChange={handleDeveloperSelect}
                      >
                        <option value="">-- Select Developer --</option>
                        {DEVELOPERS.map((dev, index) => (
                          <option key={index} value={dev}>
                            {dev}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className="fw-bold">
                        <FaFileSignature className="me-2 text-primary" /> Upload Signature
                      </Form.Label>
                      <Form.Control 
                        type="file" 
                        accept="image/*" 
                        onChange={handleSignatureUpload} 
                        className="mb-3"
                      />
                      {state.signature && (
                        <div className="text-center">
                          <p className="mb-2 fw-medium">Signature Preview:</p>
                          <Image
                            src={state.signature}
                            alt="Signature"
                            fluid
                            style={{ maxHeight: "80px" }}
                            className="border rounded p-2"
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Card.Body>
                </Card>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              try {
                // Here you would typically update the quotation with the signature and developer
                // For example: await axios.put(`/updateQuotation/${state.selectedQuote._id}`, { developer: state.selectedDeveloper, signature });
                alert(`Submitted!\nDeveloper: ${state.selectedDeveloper || "None"}`);
                handleCloseModal();
                fetchQuotations(); // Refresh the list
              } catch (err) {
                console.error("Error submitting approval:", err);
                alert("Failed to submit approval");
              }
            }}
            disabled={!state.selectedDeveloper || !state.signature}
          >
            Submit Approval
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add New Quotation Modal */}
      <Modal show={state.showAddModal} onHide={() => updateState({ showAddModal: false })} centered size="lg">
        <Modal.Header closeButton className="bg-primary text-white border-bottom-0">
          <Modal.Title className="fw-semibold d-flex align-items-center gap-2">
            <FaPlus /> Create New Quotation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">
          <Form>
            <Row className="g-4">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Quotation Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={newQuote.title}
                    onChange={handleNewQuoteChange}
                    placeholder="Enter quotation title"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Amount</Form.Label>
                  <Form.Control
                    type="text"
                    name="amount"
                    value={newQuote.amount}
                    onChange={handleNewQuoteChange}
                    placeholder="Enter amount (e.g. $5,000)"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={newQuote.status}
                    onChange={handleNewQuoteChange}
                  >
                    <option value="Requested">Requested</option>
                    <option value="Approved">Approved</option>
                    <option value="In Progress">In Progress</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Created Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={newQuote.date}
                    onChange={handleNewQuoteChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Valid Until</Form.Label>
                  <Form.Control
                    type="date"
                    name="validUntil"
                    value={newQuote.validUntil}
                    onChange={handleNewQuoteChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Assign Developer</Form.Label>
                  <Form.Select
                    name="developer"
                    value={newQuote.developer}
                    onChange={handleNewQuoteChange}
                  >
                    <option value="">-- Select Developer --</option>
                    {DEVELOPERS.map((dev, index) => (
                      <option key={index} value={dev}>{dev}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={newQuote.description}
                    onChange={handleNewQuoteChange}
                    placeholder="Detailed description of the quotation..."
                    style={{ resize: "none" }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button variant="outline-secondary" onClick={() => updateState({ showAddModal: false })}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddNewQuote}
            disabled={
              !newQuote.title ||
              !newQuote.amount ||
              !newQuote.date ||
              !newQuote.validUntil ||
              !newQuote.description ||
              !newQuote.developer
            }
          >
            Create Quotation
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuotationTable;